import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './Slider.css';

const API_URL ='https://api.sohanihandicrafts.com:5010'; // Change to your backend port

// --- EFFECT SUB-COMPONENTS (Updated to use dynamic URL) ---

const BlockEffect = ({ image }) => (
  <div className="effect-grid">
    {[...Array(24)].map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.3 } }}
        transition={{ duration: 0.6, delay: i * 0.04, ease: "backOut" }}
        className="grid-cell"
        style={{ 
          backgroundImage: `url(${image})`,
          backgroundPosition: `${(i % 6) * 20}% ${Math.floor(i / 6) * 33.33}%`,
          backgroundSize: '600% 400%'
        }}
      />
    ))}
  </div>
);

const LineEffect = ({ image }) => (
  <div className="effect-lines">
    {[...Array(10)].map((_, i) => (
      <motion.div
        key={i}
        initial={{ height: "0%" }}
        animate={{ height: "100%" }}
        exit={{ height: "0%", transition: { duration: 0.3 } }}
        transition={{ duration: 0.8, delay: i * 0.1, ease: "circOut" }}
        className="line-cell"
        style={{ 
          backgroundImage: `url(${image})`,
          backgroundPosition: `${i * 11.1}% center`,
          backgroundSize: '1000% 100%',
          left: `${i * 10}%`
        }}
      />
    ))}
  </div>
);

const ShutterEffect = ({ image }) => (
  <div className="effect-shutters">
    {[...Array(8)].map((_, i) => (
      <motion.div
        key={i}
        initial={{ width: "0%" }}
        animate={{ width: "100%" }}
        exit={{ width: "0%", transition: { duration: 0.3 } }}
        transition={{ duration: 0.8, delay: i * 0.1, ease: "easeInOut" }}
        className="shutter-cell"
        style={{ 
          backgroundImage: `url(${image})`,
          backgroundPosition: `center ${i * 14.2}%`,
          backgroundSize: '100% 800%',
          top: `${i * 12.5}%`
        }}
      />
    ))}
  </div>
);

// --- MAIN SLIDER COMPONENT ---
const HeroSlider = () => {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  // 1. Fetch Slides from Backend
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/slider/all`);
        setSlides(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching hero slides:", err);
        setLoading(false);
      }
    };
    fetchSlides();
  }, []);

  // 2. Auto-scroll logic
  useEffect(() => {
    if (slides.length === 0) return;
    const timer = setInterval(() => nextSlide(), 7000);
    return () => clearInterval(timer);
  }, [current, slides]);

  const nextSlide = () => {
    if (slides.length === 0) return;
    setCurrent(current === slides.length - 1 ? 0 : current + 1);
  };

  const prevSlide = () => {
    if (slides.length === 0) return;
    setCurrent(current === 0 ? slides.length - 1 : current - 1);
  };

  // Helper to decide which effect to render
  const renderEffect = () => {
    const slide = slides[current];
    const fullImageUrl = `${API_URL}/${slide.image}`;
    
    if (slide.effect === "blocks") return <BlockEffect image={fullImageUrl} />;
    if (slide.effect === "lines") return <LineEffect image={fullImageUrl} />;
    return <ShutterEffect image={fullImageUrl} />;
  };

  // While data is loading
  if (loading) return <div className="hero-loader">Loading Woodcraft Visuals...</div>;
  
  // If no slides exist in DB
  if (slides.length === 0) return null;

  return (
    <div className="hero-container">
      <AnimatePresence mode='wait'>
        <div key={current} className="hero-slide-wrapper">
          
          {/* Base Blurred Background */}
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="hero-bg-base"
            style={{ backgroundImage: `url(${API_URL}/${slides[current].image})` }}
          />

          {/* Dynamic Animation Layer */}
          <div className="effect-layer">
            {renderEffect()}
          </div>


         
        </div>
      </AnimatePresence>

      {/* Controls */}
      <div className="slider-controls">
        <button onClick={prevSlide}><ChevronLeft size={24}/></button>
        <div className="slide-indicators">
          {slides.map((_, idx) => (
            <div 
              key={idx} 
              className={`dot ${current === idx ? 'active' : ''}`}
              onClick={() => setCurrent(idx)}
            />
          ))}
        </div>
        <button onClick={nextSlide}><ChevronRight size={24}/></button>
      </div>
    </div>
  );
};

export default HeroSlider;